import React, { useEffect, useState, useCallback } from "react";
import { Modal, Button, Form, Table, Container, InputGroup } from "react-bootstrap";
import { FiSearch, FiEdit2, FiPlus } from "react-icons/fi";
import { createDepartment, getAllDepartments, updateDepartment } from "../../../api/onboarddeboard";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const Department = () => {
  const token = localStorage.getItem("token");

  const [departments, setDepartments] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [editId, setEditId] = useState(null);

  const [departmentName, setDepartmentName] = useState("");
  const [headOfDept, setHeadOfDept] = useState("");
  const [isScrum, setIsScrum] = useState(false);
  const [departmentError, setDepartmentError] = useState("");

  const fetchDepartments = useCallback(async () => {
    try {
      const res = await getAllDepartments(token);
      if (res.status === 200) {
        setDepartments(res.data);
      }
    } catch (err) {
      console.error(err);
    }
  }, [token]);

  useEffect(() => {
    fetchDepartments();
  }, [fetchDepartments]);

  const handleOpenAddModal = () => {
    setEditId(null);
    setDepartmentName("");
    setHeadOfDept("");
    setIsScrum(false);
    setDepartmentError("");
    setShowModal(true);
  };

  const handleEdit = (dept) => {
    setEditId(dept.id);
    setDepartmentName(dept.departmentName || "");
    setHeadOfDept(dept.headOfDepartment || dept.headName || "");
    setIsScrum(!!(dept.isScrum ?? dept.scrum));
    setDepartmentError("");
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setDepartmentName("");
    setHeadOfDept("");
    setIsScrum(false);
    setDepartmentError("");
    setEditId(null);
  };

  const validate = (value) => {
    if (!value || value.trim() === "") return "Department Name is required";
    return "";
  };

  const handleSubmit = async () => {
    const error = validate(departmentName);
    if (error) {
      setDepartmentError(error);
      return;
    }

    if (editId) {
      const existing = departments.find((d) => d.id === editId);
      if (
        existing &&
        existing.departmentName === departmentName.trim() &&
        (existing.headOfDepartment || existing.headName || "") === headOfDept.trim() &&
        !!(existing.isScrum ?? existing.scrum) === isScrum
      ) {
        toast.warn("No changes found for updation");
        return;
      }
    }

    try {
      const payload = {
        departmentName: departmentName.trim(),
        headOfDepartment: headOfDept.trim(),
        isScrum: isScrum,
      };

      if (editId) {
        await updateDepartment(editId, payload, token);
        toast.success("Department updated successfully");
      } else {
        await createDepartment(payload, token);
        toast.success("Department created successfully");
      }
      handleCloseModal();
      fetchDepartments();
    } catch (err) {
      toast.error("Something went wrong");
    }
  };

  const filtered = departments.filter((d) =>
    d.departmentName.toLowerCase().includes(searchText.toLowerCase())
  );

  return (
    <Container fluid className="p-4">

      {/* Header */}
      <div className="d-flex justify-content-between align-items-start mb-2 flex-wrap gap-2">
        <div>
          <h5 className="page-main-heading">Department</h5>
          <small className="page-sub-heading">Manage company departments and structure</small>
        </div>
        <Button
          onClick={handleOpenAddModal}
          style={{
            background: "#2E5CB8",
            border: "none",
            borderRadius: "8px",
            fontWeight: 500,
            padding: "8px 18px",
          }}
        >
          <FiPlus className="me-1" /> Add Department
        </Button>
      </div>

      {/* Search */}
      <div className="mb-3 mt-3" style={{ maxWidth: 340 }}>
        <InputGroup>
          <InputGroup.Text style={{ background: "#fff" }}>
            <FiSearch color="#888" />
          </InputGroup.Text>
          <Form.Control
            placeholder="Search department"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            style={{ borderLeft: "none" }}
          />
        </InputGroup>
      </div>

      {/* Table */}
      <div style={{ background: "#fff", borderRadius: 10, boxShadow: "0 1px 4px rgba(0,0,0,0.07)", overflow: "hidden" }}>
        <div style={{ overflowX: "auto" }}>
          <Table hover responsive className="mb-0 align-middle" style={{ minWidth: 500 }}>
            <thead>
              <tr style={{ background: "#f8f9fa", borderBottom: "2px solid #e9ecef" }}>
                <th style={{ padding: "14px 20px", fontWeight: 600, color: "#444" }}>Department Name</th>
                <th style={{ padding: "14px 20px", fontWeight: 600, color: "#444", textAlign: "center" }}>Head of Department</th>
                <th style={{ padding: "14px 20px", fontWeight: 600, color: "#444", textAlign: "center" }}>Employee Count</th>
                <th style={{ padding: "14px 20px", fontWeight: 600, color: "#444", textAlign: "center" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan="4" className="text-center py-5 text-muted">No departments found</td>
                </tr>
              ) : (
                filtered.map((dept) => (
                  <tr key={dept.id}>
                    <td style={{ padding: "14px 20px", fontWeight: 500 }}>{dept.departmentName}</td>
                    <td style={{ padding: "14px 20px", textAlign: "center", color: "#888" }}>
                      {dept.headOfDepartment || dept.headName || "—"}
                    </td>
                    <td style={{ padding: "14px 20px", textAlign: "center" }}>
                      {dept.employeeCount != null ? dept.employeeCount : "0"}
                    </td>
                    <td style={{ padding: "14px 20px", textAlign: "center" }}>
                      <button
                        onClick={() => handleEdit(dept)}
                        style={{ background: "none", border: "none", cursor: "pointer", color: "#4466ee" }}
                        title="Edit"
                      >
                        <FiEdit2 size={17} />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>
        </div>
      </div>

      {/* Add / Edit Modal */}
      <Modal show={showModal} onHide={handleCloseModal} centered size="md">
        <Modal.Header
          closeButton
          style={{
            borderBottom: "none",
            paddingBottom: 0,
            paddingTop: 24,
            paddingLeft: 28,
            paddingRight: 28,
          }}
        >
          <Modal.Title style={{ fontSize: "1.2rem", fontWeight: 700, color: "#1a1a2e" }}>
            {editId ? "Edit Department" : "Add Department"}
          </Modal.Title>
        </Modal.Header>

        <div style={{ paddingLeft: 28, paddingRight: 28, paddingTop: 4 }}>
          <p style={{ color: "#6b7280", fontSize: 13, margin: 0 }}>
            {editId ? "Update the department details." : "Fill in the details to add a new department."}
          </p>
        </div>

        <Modal.Body style={{ padding: "20px 28px 8px 28px" }}>

          {/* Department Name */}
          <Form.Group className="mb-3">
            <Form.Label style={{ fontWeight: 600, fontSize: 14, color: "#1a1a2e" }}>
              Department Name<span style={{ color: "#e53e3e" }}>*</span>
            </Form.Label>
            <Form.Control
              type="text"
              placeholder="e.g. Engineering"
              value={departmentName}
              onChange={(e) => {
                setDepartmentName(e.target.value);
                setDepartmentError("");
              }}
              isInvalid={!!departmentError}
              style={{
                borderRadius: 8,
                height: 46,
                fontSize: 14,
                background: "#f0f4ff",
                border: "1px solid #e5e7eb",
              }}
            />
            {departmentError && (
              <Form.Control.Feedback type="invalid">
                {departmentError}
              </Form.Control.Feedback>
            )}
          </Form.Group>

          {/* Head of Department */}
          <Form.Group className="mb-3">
            <Form.Label style={{ fontWeight: 600, fontSize: 14, color: "#1a1a2e" }}>
              Head of Department
            </Form.Label>
            <Form.Control
              type="text"
              placeholder=""
              value={headOfDept}
              onChange={(e) => setHeadOfDept(e.target.value)}
              style={{
                borderRadius: 8,
                height: 46,
                fontSize: 14,
                background: "#f0f4ff",
                border: "1px solid #e5e7eb",
              }}
            />
          </Form.Group>

          {/* Is Scrum */}
          <Form.Group className="mb-3">
            <Form.Check
              type="switch"
              id="is-scrum-switch"
              label="Is Scrum"
              checked={isScrum}
              onChange={(e) => setIsScrum(e.target.checked)}
              style={{ fontWeight: 600, fontSize: 14, color: "#1a1a2e" }}
            />
          </Form.Group>

        </Modal.Body>

        <Modal.Footer style={{ borderTop: "none", padding: "8px 28px 24px 28px", gap: 12 }}>
          <Button
            variant="outline-secondary"
            onClick={handleCloseModal}
            style={{
              borderRadius: 8,
              minWidth: 100,
              fontWeight: 500,
              color: "#374151",
              borderColor: "#d1d5db",
              background: "#fff",
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            style={{
              borderRadius: 8,
              minWidth: 100,
              fontWeight: 600,
              background: "#2E5CB8",
              border: "none",
              color: "#fff",
            }}
          >
            {editId ? "Update" : "Save"}
          </Button>
        </Modal.Footer>
      </Modal>

      <ToastContainer />
    </Container>
  );
};

export default Department;